import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Package, Users, FileText, BarChart3, Wrench, Activity, Plus, ArrowUpRight } from 'lucide-react';
import { AppLayout } from '@/components/AppLayout';
import { Link } from 'react-router-dom';
import { usePermissions, PERMISSIONS } from '@/hooks/usePermissions';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ProductionDashboard } from '@/components/dashboard/ProductionDashboard';
import { SampleDataSeeder } from '@/components/dashboard/SampleDataSeeder';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { DashboardStatsGridSkeleton, ProductionDashboardSkeleton } from '@/components/ui/skeletons';
import React from 'react';

const Dashboard = () => {
  const { user, signOut, hasPermission, canAccessPage } = usePermissions();

  // Fetch dashboard data
  const { data: inventoryStats, isLoading: inventoryLoading } = useQuery({
    queryKey: ['dashboard-inventory'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('inventory_items')
        .select('type, current_stock, min_stock_level');
      if (error) throw error;
      
      const totalItems = data.length;
      const lowStockItems = data.filter(item => 
        item.current_stock <= (item.min_stock_level || 0)
      ).length;
      const rawMaterials = data.filter(item => item.type === 'raw_material').length;
      const finishedProducts = data.filter(item => item.type === 'finished_product').length;
      
      return { totalItems, lowStockItems, rawMaterials, finishedProducts };
    },
  });

  const { data: customerStats, isLoading: customerLoading } = useQuery({
    queryKey: ['dashboard-customers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('customers')
        .select('status');
      if (error) throw error;
      
      const totalCustomers = data.length;
      const activeCustomers = data.filter(c => c.status === 'active').length;
      
      return { totalCustomers, activeCustomers };
    },
  });

  const { data: orderStats, isLoading: orderLoading } = useQuery({
    queryKey: ['dashboard-orders', new Date().getFullYear(), new Date().getMonth()],
    queryFn: async () => {
      // Get current month start and end dates
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
      
      const { data, error } = await supabase
        .from('orders')
        .select('status, total_amount, created_at')
        .gte('created_at', monthStart.toISOString())
        .lte('created_at', monthEnd.toISOString());
      if (error) throw error;
      
      const totalOrders = data.length;
      const pendingOrders = data.filter(o => o.status === 'pending').length;
      const completedOrders = data.filter(o => o.status === 'complete');
      const monthToDateRevenue = completedOrders.reduce((sum, order) => sum + (order.total_amount || 0), 0);
      
      return { totalOrders, pendingOrders, monthToDateRevenue };
    },
  });

  const isStatsLoading = inventoryLoading || customerLoading || orderLoading;

  const dashboardStats = [
    {
      title: "Month to Date Wholesale Revenue",
      value: `$${orderStats?.monthToDateRevenue?.toFixed(2) || '0.00'}`,
      description: "Revenue from completed orders",
      icon: BarChart3,
      permission: PERMISSIONS.VIEW_DASHBOARD,
    },
    {
      title: "Active Customers",
      value: customerStats?.activeCustomers?.toString() || '0',
      description: `${customerStats?.totalCustomers || 0} total customers`,
      icon: Users,
      permission: PERMISSIONS.VIEW_DASHBOARD,
    },
    {
      title: "Inventory Items",
      value: inventoryStats?.totalItems?.toString() || '0',
      description: `${inventoryStats?.lowStockItems || 0} items low stock`,
      icon: Package,
      permission: PERMISSIONS.VIEW_DASHBOARD,
    },
    {
      title: "Pending Orders",
      value: orderStats?.pendingOrders?.toString() || '0',
      description: `${orderStats?.totalOrders || 0} total orders`,
      icon: FileText,
      permission: PERMISSIONS.VIEW_DASHBOARD,
    },
  ];

  return (
    <AppLayout currentPage="dashboard">
      <div className="space-y-4 sm:space-y-6">
        <div className="space-y-1">
          <h1 className="mobile-heading">Dashboard</h1>
          <p className="mobile-body text-muted-foreground">
            Overview of your inventory management system
          </p>
        </div>

        {/* Stats Grid */}
        {isStatsLoading ? (
          <DashboardStatsGridSkeleton />
        ) : (
          <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            {dashboardStats
              .filter(stat => hasPermission(stat.permission))
              .map((stat) => (
              <Card key={stat.title} className="stat-card touch-target">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-xs sm:text-sm font-medium leading-tight">
                    {stat.title}
                  </CardTitle>
                  <stat.icon className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground flex-shrink-0" />
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="text-xl sm:text-2xl font-bold">{stat.value}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {stat.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Quick Actions */}
        <Card className="mobile-card">
          <CardHeader className="pb-3 sm:pb-4">
            <CardTitle className="text-base sm:text-lg">Quick Actions</CardTitle>
            <CardDescription className="text-sm">
              Get started with these common tasks
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center p-4 sm:p-6 text-center">
                <Package className="h-6 w-6 sm:h-8 sm:w-8 text-muted-foreground mb-2 sm:mb-4" />
                <h3 className="font-semibold text-sm sm:text-base mb-1 sm:mb-2">Add Inventory Item</h3>
                <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">
                  Add raw materials or finished products
                </p>
                <Button asChild size="sm" className="w-full sm:w-auto touch-target">
                  <Link to="/inventory">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Item
                  </Link>
                </Button>
              </CardContent>
            </Card>
            
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center p-4 sm:p-6 text-center">
                <Users className="h-6 w-6 sm:h-8 sm:w-8 text-muted-foreground mb-2 sm:mb-4" />
                <h3 className="font-semibold text-sm sm:text-base mb-1 sm:mb-2">Add Customer</h3>
                <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">
                  Create new customer profile
                </p>
                <Button asChild size="sm" className="w-full sm:w-auto touch-target">
                  <Link to="/wholesale">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Customer
                  </Link>
                </Button>
              </CardContent>
            </Card>
            
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center p-4 sm:p-6 text-center">
                <BarChart3 className="h-6 w-6 sm:h-8 sm:w-8 text-muted-foreground mb-2 sm:mb-4" />
                <h3 className="font-semibold text-sm sm:text-base mb-1 sm:mb-2">View Reports</h3>
                <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">
                  Analytics and insights
                </p>
                <Button asChild size="sm" variant="outline" className="w-full sm:w-auto touch-target">
                  <Link to="/reports">
                    <ArrowUpRight className="h-4 w-4 mr-2" />
                    View Reports
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </CardContent>
        </Card>

        {/* Production Dashboard */}
        <div className="space-y-3 sm:space-y-4">
          <h2 className="mobile-subheading">Production Overview</h2>
          <ErrorBoundary>
            <React.Suspense fallback={<ProductionDashboardSkeleton />}>
              <ProductionDashboard />
            </React.Suspense>
          </ErrorBoundary>
        </div>
      </div>
    </AppLayout>
  );
};

export default Dashboard;